<template>
  <div>
    <div class="flex justify-between gap-2 items-center truncate py-1">
      <CButton
        v-if="activeFile && showResultsMessage"
        v-tooltip="t('canvasUI.contentSelectorApp.content.back')"
        circle
        @click="() => setState({ activeFile: null, selectedSection: 'all' })"
      >
        <template #icon>
          <CIcon icon="chevron-left" size="16" />
        </template>
      </CButton>

      <NDropdown
        v-if="isContentDropdownEnabled && navigationOptions.length > 1"
        :on-select="(_, selectedOption) => onNavigationOptionSelect(selectedOption)"
        :options="navigationOptions"
        placement="bottom-start"
        :trigger="dropdownTriggerMethod"
      >
        <CButton class="p-2" icon-placement="left" :style="{ border: `1px solid ${themeVars.primary4}` }">
          <template #icon>
            <CIcon class="" icon="ellipsis" size="16px" />
          </template>
        </CButton>
      </NDropdown>
      <div v-if="showResultsMessage" class="text-lg tracking-[.25px]">
        {{ activeFile ? activeFile.name : t('canvasUI.contentSelectorApp.content.results') }}
      </div>
      <template v-else>
        <NBreadcrumb
          v-if="selectedSection === 'all' && breadcrumbs.length"
          class="overflow-x-auto overflow-y-hidden"
          separator=">"
        >
          <NBreadcrumbItem
            v-for="(b, idx) in breadcrumbs"
            :key="b.id"
            class="content__breadcrumb__item"
            :class="{ 'cursor-pointer': b.id !== activeFolder?.id }"
            :clickable="idx !== breadcrumbs.length - 1"
            @click="onBreadcrumbClick(b as MiniFolder, idx !== breadcrumbs.length - 1)"
          >
            {{ !idx ? t('canvasUI.contentSelectorApp.content.allContent') : b.name }}
          </NBreadcrumbItem>
        </NBreadcrumb>
        <div v-else-if="selectedSection === 'favorites'" class="p-1 content-selector__section-title">
          {{ t('canvasUI.contentSelectorApp.content.favorites') }}
        </div>
        <div v-else-if="selectedSection === 'recent'" class="p-1 content-selector__section-title">
          {{ t('canvasUI.contentSelectorApp.content.recent') }}
        </div>
        <div v-else-if="selectedSection === 'trash'" class="p-1 content-selector__section-title">
          {{ t('canvasUI.contentSelectorApp.content.trash') }}
        </div>
      </template>
      <div class="flex items-center gap-2 ml-a">
        <NDropdown
          v-if="selectedSection === 'all'"
          class="sort-dropdown"
          :on-select="(_, selectedOption) => onSortOptionSelect(selectedOption)"
          :options="sortOptions"
          placement="bottom-end"
          trigger="click"
        >
          <CButton v-tooltip="getCurrentSortLabel()" class="p-2" icon-placement="left">
            {{ t('canvasUI.browserApp.content.sortBy') }}: {{ getCurrentSortLabel() }}
          </CButton>
        </NDropdown>
        <CButton v-tooltip="t('canvasUI.contentSelectorApp.content.refresh')" circle @click="fetchSectionData">
          <template #icon>
            <CIcon icon="arrow-rotate-right" size="16" />
          </template>
        </CButton>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type ComputedRef, h, inject } from 'vue'
import { useI18n } from 'vue-i18n'
import { isEmpty } from 'lodash-es'
import { type MenuOption, NBreadcrumb, NBreadcrumbItem, NDropdown } from 'naive-ui'

import CButton from '@lib/components/CButton/CButton.vue'
import CIcon from '@lib/components/CIcon/CIcon.vue'

import type { MiniFolder } from '@openapi'
import useThemeVars from '@lib/composables/useThemeVars'
import { type BrowserSectionType } from '@content-selector/stores/app'
import { useStore, useStoreApi } from '@content-selector/composables/useStore'
import type { LaunchDarklyEnv } from '@lib/types/launchDarkly.types'
import type { InstanceSettings, OrganizationSettings } from '@lib/main.lib'
import useFolderNameDescription from '@lib/composables/useFolderNameDescription'

const {
  activeFolder,
  activeFile,
  setState,
  selectedSection,
  setSelectedSection,
  fetchSectionData,
  searchMetadataFilters,
  search,
  selectedSortOption,
  setSortOption,
} = useStore()
const { state: api } = useStoreApi()
const { t } = useI18n()
const themeVars = useThemeVars()

const launchDarkly = inject<ComputedRef<LaunchDarklyEnv>>(
  'launchDarkly',
  computed(() => ({})),
)

const pitcherSettings = inject<ComputedRef<OrganizationSettings & InstanceSettings>>(
  'pitcherSettings',
  computed(() => ({})),
)

const enabledRecents = computed(() => pitcherSettings.value?.enable_recents ?? false)
const enabledFavorites = computed(() => pitcherSettings.value?.enable_favorites ?? false)
const showResultsMessage = computed(() => search.value || !isEmpty(searchMetadataFilters.value))
const { parseNameOnly } = useFolderNameDescription()

const breadcrumbs = computed(() => [
  ...(activeFolder.value?.path ?? []).map((folder) => ({
    ...folder,
    name: parseNameOnly(folder.name || ''),
  })),
  {
    id: activeFolder.value?.id,
    name: parseNameOnly(activeFolder.value?.name || ''),
  },
  ...(activeFile.value ? [{ name: activeFile.value.name }] : []),
])

const dropdownTriggerMethod = window.matchMedia('(any-hover: none)').matches ? 'click' : 'hover'

const navigationOptions = [
  {
    label: t('canvasUI.contentSelectorApp.content.allContent'),
    value: 'all',
    icon: () => h(CIcon, { icon: 'folder' }),
  },
]

if (enabledRecents.value) {
  navigationOptions.push({
    label: t('canvasUI.contentSelectorApp.content.recent'),
    value: 'recent',
    icon: () => h(CIcon, { icon: 'clock' }),
  })
}

if (enabledFavorites.value) {
  navigationOptions.push({
    label: t('canvasUI.contentSelectorApp.content.favorites'),
    value: 'favorites',
    icon: () => h(CIcon, { icon: 'heart' }),
  })
}

const isContentDropdownEnabled = computed(() => launchDarkly.value?.enable_content_type_change_in_content_selector)

const getSortOptionProps = (value: string) => ({
  style:
    selectedSortOption.value === value
      ? {
          backgroundColor: themeVars.value.primary5,
        }
      : {},
})

const sortOptions = computed(() => [
  {
    label: t('canvasUI.browserApp.content.sortOptions.nameAsc'),
    value: 'nameAsc',
    key: 'nameAsc',
    icon: () => h(CIcon, { icon: 'arrow-down-a-z', size: '16px' }),
    props: getSortOptionProps('nameAsc'),
  },
  {
    label: t('canvasUI.browserApp.content.sortOptions.nameDesc'),
    value: 'nameDesc',
    key: 'nameDesc',
    icon: () => h(CIcon, { icon: 'arrow-down-z-a', size: '16px' }),
    props: getSortOptionProps('nameDesc'),
  },
  {
    label: t('canvasUI.browserApp.content.sortOptions.modifiedAsc'),
    value: 'modifiedAsc',
    key: 'modifiedAsc',
    icon: () => h(CIcon, { icon: 'calendar-arrow-up', size: '16px' }),
    props: getSortOptionProps('modifiedAsc'),
  },
  {
    label: t('canvasUI.browserApp.content.sortOptions.modifiedDesc'),
    value: 'modifiedDesc',
    key: 'modifiedDesc',
    icon: () => h(CIcon, { icon: 'calendar-arrow-down', size: '16px' }),
    props: getSortOptionProps('modifiedDesc'),
  },
])

function getCurrentSortLabel() {
  const currentOption = sortOptions.value.find((option) => option.value === selectedSortOption.value)
  return currentOption?.label || sortOptions.value[0]?.label || 'Name (A-Z)'
}

function onSortOptionSelect(selection: MenuOption) {
  setSortOption(selection.value as any)
}

async function onBreadcrumbClick(item: MiniFolder, clickable: boolean) {
  if (!clickable) return

  setState({ activeFolder: await api.folderItemsFetcher?.(item.id), activeFile: null })
}

function onNavigationOptionSelect(selection: MenuOption) {
  setSelectedSection(selection.value as BrowserSectionType)
}
</script>

<style scoped lang="scss">
.content-selector__section-title {
  font-size: 18px;
  line-height: 28px;
  letter-spacing: 0.25px;
  color: v-bind('themeVars.primary');
}
</style>
